import React, { FunctionComponent } from 'react';
import {
  ButtonBase,
  Checkbox,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import moment from 'moment';
import {
  IPenaltyWaiver,
  PENALTY_WAIVER_APPEAL_STATUSES,
  PENALTY_WAIVER_PAYMENT_STATUSES,
  PENALTY_WAIVER_TYPES,
} from '../../../../../../store/V2/PenaltyWaiverSlice';
import { IUser } from '../../../../../../store/UserSlice';
import { getFormatedTableLabels, getShortName } from '../../../../../../helper';
import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../../../components/V2/Table/type1/TableStyles';
import { MEETING_SECTION_PAYMENT_TYPES } from '../../../../../../store/V2/SectionSlice';
import { ICourse } from '../../../../../../store/V2/CoursesSlice';

interface PenaltyWaiverTableRowProps {
  penaltyWaiver: IPenaltyWaiver;
  onView: () => void;
  onStudentsClick: () => void;
  onAppealClick: () => void;
}

const actionButtonStyles: SxProps = {
  borderRadius: '8px',
  padding: '10px 12px 10px 12px',
  fontWeight: 600,
  fontSize: '14px',
  maxHeight: '36px',
  backgroundColor: '#006C9C',
  color: '#F8FAFC',
  width: 'fit-content',
};

const PenaltyWaiverTableRow: FunctionComponent<PenaltyWaiverTableRowProps> = ({
  penaltyWaiver,
  onView,
  onStudentsClick,
  onAppealClick,
}) => {
  const createdAtMoment = moment(penaltyWaiver.createdAt);
  const createdAtDate = createdAtMoment.format('ddd MMM DD YYYY');
  const createdAtTime = createdAtMoment.format('hh:mm A');
  const classStartDateMoment = moment(penaltyWaiver.startDate);
  const classStartDate = classStartDateMoment.format('ddd MMM DD YYYY');
  const classStartTime = classStartDateMoment.format('hh:mm A');
  const classEndDateMoment = moment(penaltyWaiver.endDate);
  const classEndDate = classEndDateMoment.format('ddd MMM DD YYYY');
  const classEndTime = classEndDateMoment.format('hh:mm A');
  const lessonType = penaltyWaiver.lessonType ? penaltyWaiver.lessonType : '-';
  const paymentType = penaltyWaiver.paymentType
    ? penaltyWaiver.paymentType
    : '-';

  const courseData = penaltyWaiver.course as ICourse;
  const studentList = (penaltyWaiver.students || []) as IUser[];

  let amountTexts = '-';
  let penaltyAppealStatusColor: string | undefined;
  let studentsButtonName = 'No students';

  if (penaltyWaiver.amount && penaltyWaiver.currency && penaltyWaiver.type) {
    amountTexts = `(${
      penaltyWaiver.type === PENALTY_WAIVER_TYPES.ADDITION ? '+' : '-'
    }) ${penaltyWaiver.currency} ${penaltyWaiver.amount.toFixed(2)}`;
  }

  if (studentList.length) {
    studentsButtonName = getFormatedTableLabels({
      labels: studentList.map((studentItem) =>
        getShortName(studentItem.firstname, studentItem.lastname)
      ),
    });
  }

  switch (penaltyWaiver.appealStatus) {
    case PENALTY_WAIVER_APPEAL_STATUSES.PENDING:
      penaltyAppealStatusColor = 'warning.main';
      break;

    case PENALTY_WAIVER_APPEAL_STATUSES.ACCEPTED:
      penaltyAppealStatusColor = 'success.main';
      break;

    case PENALTY_WAIVER_APPEAL_STATUSES.REJECTED:
      penaltyAppealStatusColor = 'error.main';
      break;

    default:
      penaltyAppealStatusColor = undefined;
      break;
  }

  return (
    <StyledTableRow hover key={penaltyWaiver._id}>
      <StyledTableCell>
        <Stack>
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            {createdAtDate}
          </Typography>
          <Typography fontSize="12px" color="#94A3B8">
            {createdAtTime}
          </Typography>
        </Stack>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          textTransform="capitalize"
        >
          {courseData && courseData.title ? courseData.title : '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Stack>
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            {classStartDate}
          </Typography>
          <Typography fontSize="12px" color="#94A3B8">
            {classStartTime}
          </Typography>
        </Stack>
      </StyledTableCell>
      <StyledTableCell>
        <Stack>
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            {classEndDate}
          </Typography>
          <Typography fontSize="12px" color="#94A3B8">
            {classEndTime}
          </Typography>
        </Stack>
      </StyledTableCell>
      <StyledTableCell>
        <ButtonBase
          sx={{
            textDecoration: 'underline',
            textAlign: 'unset',
            width: 'fit-content',
          }}
          onClick={onStudentsClick}
        >
          {studentsButtonName}
        </ButtonBase>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          textTransform="capitalize"
        >
          {lessonType.toLowerCase()}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          textTransform="capitalize"
          color={
            paymentType === MEETING_SECTION_PAYMENT_TYPES.BILLABLE
              ? 'success.main'
              : 'warning.main'
          }
        >
          {paymentType.toLowerCase()}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          textTransform="capitalize"
        >
          {amountTexts}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          textTransform="capitalize"
          color={
            penaltyWaiver.paymentStatus === PENALTY_WAIVER_PAYMENT_STATUSES.PAID
              ? 'success.main'
              : '#1E293B'
          }
        >
          {penaltyWaiver.paymentStatus.toLowerCase()}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        {penaltyWaiver.appealStatus ? (
          <Stack>
            <Typography
              fontSize="14px"
              fontWeight={500}
              textTransform="capitalize"
              color={penaltyAppealStatusColor}
            >
              {penaltyWaiver.appealStatus.toLowerCase()}
            </Typography>
            <ButtonBase
              onClick={onAppealClick}
              sx={{
                textDecoration: 'underline',
                textAlign: 'unset',
                width: 'fit-content',
              }}
            >
              View
            </ButtonBase>
          </Stack>
        ) : (
          <Typography
            fontSize="14px"
            fontWeight={500}
            color="#1E293B"
            textTransform="capitalize"
          >
            -
          </Typography>
        )}
      </StyledTableCell>
      <StyledTableCell>
        <Stack alignItems="flex-end">
          <ButtonBase sx={{ ...actionButtonStyles }} onClick={onView}>
            View
          </ButtonBase>
        </Stack>
      </StyledTableCell>
    </StyledTableRow>
  );
};

export default PenaltyWaiverTableRow;
